import { usePermissions } from '@/context/PermissionsContext'
import { useParams } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import { Permission, Role } from '@droidmap/permissions-service-contract'

export interface PermissionCheckResult {
  canView: boolean
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canManageUsers: boolean
  isAdmin: boolean
  isLoading: boolean
  hasPermission: (permission: Permission, resourceId?: string) => boolean
  hasAnyPermission: (permissions: Permission[], resourceId?: string) => boolean
  hasRole: (
    role: Role,
    resourceType: 'organization' | 'project',
    resourceId?: string,
  ) => boolean
}

/**
 * Hook that provides common permission checks for the current route context
 */
export const usePermissionCheck = (): PermissionCheckResult => {
  const params = useParams({ strict: false }) as {
    orgId?: string
    orgId_?: string
    projectId?: string
  }

  // Handle both orgId and orgId_ parameter names
  const orgId = params.orgId || params.orgId_
  const projectId = params.projectId

  const { hasPermission, hasAnyPermission, hasRole, isLoading, setContext } =
    usePermissions()

  // Update permissions context when route params change
  useEffect(() => {
    setContext(orgId || null, projectId || null)
  }, [orgId, projectId, setContext])

  const permissionCheck = useMemo((): PermissionCheckResult => {
    if (isLoading) {
      return {
        canView: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canManageUsers: false,
        isAdmin: false,
        isLoading: true,
        hasPermission,
        hasAnyPermission,
        hasRole,
      }
    }

    // Check if user is admin at any level (highest permission wins)
    const isOrgAdmin = hasRole(Role.ORG_ADMIN, 'organization', orgId)
    const isProjectAdmin = hasRole(Role.PROJECT_ADMIN, 'project', projectId)
    const isAdmin = isOrgAdmin || isProjectAdmin

    // View permissions (read access)
    const canView = hasAnyPermission([
      Permission.VIEW_ORGANIZATION,
      Permission.VIEW_PROJECT,
      Permission.VIEW_ASSET,
      Permission.VIEW_ASSET_SET,
      Permission.VIEW_TASK,
      Permission.LIST_ORGANIZATIONS,
      Permission.LIST_PROJECTS,
      Permission.LIST_ASSETS,
      Permission.LIST_ASSET_SETS,
      Permission.LIST_TASKS,
    ])

    // Create permissions
    const canCreate =
      hasAnyPermission([
        Permission.CREATE_PROJECT,
        Permission.CREATE_ASSET,
        Permission.CREATE_ASSET_SET,
        Permission.CREATE_TASK,
        Permission.CREATE_GROUP,
      ]) || isAdmin

    // Update permissions
    const canUpdate =
      hasAnyPermission([
        Permission.UPDATE_ORGANIZATION,
        Permission.UPDATE_PROJECT,
        Permission.UPDATE_ASSET,
        Permission.UPDATE_ASSET_SET,
        Permission.UPDATE_TASK,
        Permission.UPDATE_GROUP,
      ]) || isAdmin

    // Delete permissions
    const canDelete =
      hasAnyPermission([
        Permission.DELETE_ORGANIZATION,
        Permission.DELETE_PROJECT,
        Permission.DELETE_ASSET,
        Permission.DELETE_ASSET_SET,
        Permission.DELETE_TASK,
        Permission.DELETE_GROUP,
      ]) || isAdmin

    // User management permissions
    const canManageUsers =
      hasAnyPermission([
        Permission.CREATE_ORGANISATION_USER,
        Permission.UPDATE_ORGANISATION_USER,
        Permission.DELETE_ORGANISATION_USER,
        Permission.CREATE_GROUP_USER,
        Permission.DELETE_GROUP_USER,
        Permission.CREATE_ORG_GRANT,
        Permission.CREATE_PROJECT_GRANT,
        Permission.DELETE_ORG_GRANT,
        Permission.DELETE_PROJECT_GRANT,
      ]) || isAdmin

    return {
      canView,
      canCreate,
      canUpdate,
      canDelete,
      canManageUsers,
      isAdmin,
      isLoading: false,
      hasPermission,
      hasAnyPermission,
      hasRole,
    }
  }, [isLoading, hasRole, hasAnyPermission, hasPermission, orgId, projectId])

  return permissionCheck
}
